import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import { useStandardizeImages } from "../data";
import { useGetPartnerOffering } from "../data/api";
import { Testimonial } from "../data/partners";
import { buildPathWithParams, useQueryString } from "../hooks";
import { useTrackProductViewedEcommerceEventOnMount } from "./analytics_ecommerce_events";
import {
  useLocationQuery,
  usePartnerCatalogQueryMaybe,
  useQuoteQuery,
  useSpaceOfferingsQuery,
  useSpaceQuery,
} from "./api";
import { MultiPassCheckoutBoxDesktop } from "./checkout_box_desktop";
import { MultiPassCheckoutBoxMobile } from "./checkout_box_mobile";
import { useCurrentQueryString, useSyncQueryString } from "./offering_helpers";
import {
  normalizeMultiPassOrderParams,
  toQuoteInput,
  useMultiPassOrderParamsHandlers,
} from "./order_params";
import { PartnerConfig } from "./partner_config";
import {
  GeneralInformation,
  OfferingDetailsBase,
} from "./template_offering_details_base";
import { FAQ } from "./template_offering_details_common";

interface OfferingDetailsMultiPassParams {
  offeringID: string;
}

export interface OfferingDetailsMultiPassQueryParams {
  spaceID?: string;
  startDate?: string;
  endDate?: string;
  addOnID?: string;
  spaceOfferingID?: string;
  fromCatalog?: string;
  promoCode?: string;
}

interface OfferingDetailsMultiPassProps {
  showTalkWithUsPrompt?: boolean;
  showBackButton?: boolean;
  showAddress?: boolean;
  showDirections: boolean;
  partnerConfig: PartnerConfig;
  data?: {
    description?: React.ReactNode;
    title?: string;
    amenitiesDescription?: string;
    faqs?: FAQ[];
    testimonials?: Testimonial[];
    generalInfo?: GeneralInformation;
  };
}

export function OfferingDetailsMultiPass(props: OfferingDetailsMultiPassProps) {
  const {
    showBackButton,
    partnerConfig,
    data,
    showTalkWithUsPrompt,
    showAddress = false,
    showDirections,
  } = props;
  const pageParams = useParams<OfferingDetailsMultiPassParams>();
  const queryParams = useQueryString<OfferingDetailsMultiPassQueryParams>();
  const offeringID = pageParams.offeringID;
  const partnerOffering = useGetPartnerOffering(offeringID);
  const partnerCatalog = usePartnerCatalogQueryMaybe(
    partnerOffering.partner.partnerSlug,
    queryParams.fromCatalog
  );
  const firstPartnerOfferingLocation = partnerOffering.offeringLocations[0];
  const defaultSpaceID = firstPartnerOfferingLocation.spaceID;
  const {
    orderParams,
    handleChangePromoCode,
    handleChangeLocation,
    handleChangeMultiPass,
  } = useMultiPassOrderParamsHandlers({
    ...normalizeMultiPassOrderParams({
      ...queryParams,
      promoCode: queryParams.promoCode,
      spaceID:
        partnerOffering.offeringLocations.length > 1
          ? queryParams.spaceID
          : defaultSpaceID,
    }),
  });

  useEffect(() => {
    if (partnerCatalog?.promoCode && !queryParams.promoCode) {
      handleChangePromoCode(partnerCatalog?.promoCode);
    }
  }, [handleChangePromoCode, partnerCatalog, queryParams.promoCode]);

  const history = useHistory();
  const selectedSpaceID = orderParams.spaceID;
  const selectedSpaceQuery = useSpaceQuery(selectedSpaceID || defaultSpaceID);
  const { data: space, loading } = selectedSpaceQuery;

  const { data: spaceOfferings } = useSpaceOfferingsQuery(
    selectedSpaceID,
    partnerOffering.addOns.map((addOn) => addOn.id)
  );
  const [spaceLayoutID, setSpaceLayoutID] = useState<string>();
  useEffect(() => {
    if (space) setSpaceLayoutID(space.defaultLayoutID);
  }, [space, setSpaceLayoutID]);

  const quoteInput = useMemo(
    () =>
      selectedSpaceID && spaceLayoutID
        ? toQuoteInput(
            selectedSpaceID,
            partnerOffering.partner.id,
            orderParams,
            "WL",
            {
              layoutID: spaceLayoutID,
              attendees: 1,
            }
          )
        : null,
    [selectedSpaceID, partnerOffering.partner.id, orderParams, spaceLayoutID]
  );
  const selectedLocationQuery = useLocationQuery(
    selectedSpaceID ? space?.locationID : undefined
  );
  const { data: location } = selectedLocationQuery;
  const { data: quote, loading: quoteLoading } = useQuoteQuery(quoteInput);

  const queryString = useCurrentQueryString(orderParams);
  useSyncQueryString(orderParams);

  const handleContinue = useCallback(() => {
    const pathWithParams = buildPathWithParams(
      `/offerings/checkout/${pageParams.offeringID}?${queryString}`
    );
    history.push(pathWithParams);
  }, [history, pageParams.offeringID, queryString]);

  const handlePressBack = useCallback(() => {
    if (history.length > 1) {
      history.goBack();
    } else {
      const pathWithParams = buildPathWithParams(`/offerings/${offeringID}`, {
        reset: true,
        keepOnReset: ["fromCatalog"],
      });
      history.replace(pathWithParams);
    }
  }, [history, offeringID]);

  useEffect(() => {
    if (queryParams.spaceID && !loading && !space) {
      const pathWithParams = buildPathWithParams(`/offerings/${offeringID}`);
      history.replace(pathWithParams);
    }
  }, [space, queryParams.spaceID, offeringID, history, loading]);
  const title = partnerOffering.offeringTitle || data?.title;
  const offeringName =
    space && location ? `${space.productType?.name} to ${location.name}` : "";
  useTrackProductViewedEcommerceEventOnMount(offeringName, space, location);
  const images = useStandardizeImages(
    partnerOffering,
    location,
    space,
    selectedSpaceID
  );

  return (
    <OfferingDetailsBase
      showTalkWithUsPrompt={showTalkWithUsPrompt}
      showAddress={showAddress}
      showDirections={showDirections}
      partnerOffering={partnerOffering}
      partnerOfferingID={offeringID}
      partnerConfig={partnerConfig}
      space={space}
      onPressBack={showBackButton ? handlePressBack : undefined}
      data={{
        ...data,
        ...images,
        title,
        offeringName,
        location,
      }}
      checkoutBoxDesktop={
        <MultiPassCheckoutBoxDesktop
          quoteLoading={quoteLoading}
          orderParams={orderParams}
          onChangeLocation={handleChangeLocation}
          onChangeMultiPass={handleChangeMultiPass}
          onContinue={handleContinue}
          locations={partnerOffering.offeringLocations}
          multiPasses={spaceOfferings || []}
          location={location}
          quote={quote}
          space={selectedSpaceQuery.data}
        />
      }
      checkoutBoxMobile={
        <MultiPassCheckoutBoxMobile
          orderParams={orderParams}
          selectedSpace={space}
          onChangeLocation={handleChangeLocation}
          onChangeMultiPass={handleChangeMultiPass}
          onContinue={handleContinue}
          locations={partnerOffering.offeringLocations}
          multiPasses={spaceOfferings || []}
          quote={quote}
        />
      }
    />
  );
}
